import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NewrelicEvent } from 'qp-newrelic';
import { ProfileItem, TabVm } from 'qp-ui-interface';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageType } from 'src/app/analytics/newrelic/enums/PageType';
import { NewrelicService } from 'src/app/analytics/newrelic/service/newrelic.service';
import { DataService } from '../../data.service';
import { LocalStorageKeys } from '../../shared/LocalStorageKeys';
import { AdobeService } from '../../shared/services/adobe/adobe.service';
import { ExitConfirmationService } from '../../shared/services/exit-confirmation/exit-confirmation.service';
import { LanguageService } from '../../shared/services/language/language.service';
import { LocalStorageService } from '../../shared/services/local-storage/local-storage.service';
import { NavigationLibraryService } from '../../shared/services/navigation/navigation.service';
import { ProfileService } from '../../shared/services/profiles/profile.service';
import { ScreenService } from '../../shared/services/screen/screen.service';
import { TabService } from '../../shared/services/tabs/tab.service';
import { UserStateService } from '../../shared/services/user-state/user-state.service';
import { isLoggedIn } from '../../shared/utils/LoginUtils';
import { AppClient, getAppClient } from '../../shared/utils/deviceUtils';
import { SubscriptionHandlerService } from '../../shared/services/subscription-handler.service';
import { trackByKey } from '../../shared/utils/trackBy';
import { clearTimeouts, onNavigationStart } from '../../shared/utils/timeoutUtils';
declare type MenuOption = 'tab' | 'search' | 'myhub' | 'epg' | 'my-content';
@Component({
  selector: 'app-tv-vertical-menu',
  templateUrl: './tv-vertical-menu.component.html',
  styleUrls: ['./tv-vertical-menu.component.scss'],
})
export class TvVerticalMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  public static classState = 'TvVerticalMenuComponent';
  @ViewChild('tvMenu') tvMenu: ElementRef;
  menuActive = false;
  isLoggedIn: boolean;
  tabs: TabVm[] = [];
  selectedProfile: ProfileItem;
  saved = false; // reuse strategy flag
  focusedTab = '';
  showButton: boolean;
  client: AppClient = 'dtc';
  tveImgSrc: string;
  protected readonly trackById = trackByKey<TabVm, 'id'>('id');
  private enterMenu = false;
  private destroy$ = new Subject<void>();
  private componentName = 'TvVerticalMenu';
  private timeouts: ReturnType<typeof setTimeout>[] = [];
  constructor(
    public router: Router,
    private dataService: DataService,
    private exitService: ExitConfirmationService,
    private readonly screenService: ScreenService,
    private readonly tabService: TabService,
    private readonly languageService: LanguageService,
    private readonly profileService: ProfileService,
    private readonly userState: UserStateService,
    private readonly navService: NavigationLibraryService,
    private readonly localStorage: LocalStorageService,
    private readonly changeRef: ChangeDetectorRef,
    private readonly adobeService: AdobeService,
    private readonly subscriptionHandlerService: SubscriptionHandlerService,
    private readonly newrelic: NewrelicService
  ) {}

  /**
   * Vertical index in menu
   * 0 = subscribe button
   * 1 = first menu element
   */
  get relativeVerticalIndex(): number {
    return this.showButton ? 1 : 0;
  }

  /**
   * Returns active element in document
   */
  get activeElement(): Element {
    return document.activeElement;
  }

  /**
   * Retruns the active tab value
   */
  get activeTab(): string {
    return this.dataService.activeTab;
  }

  /**
   * show back confirmation screen
   */
  get showBack(): boolean {
    return !this.exitService.isExitConfirmationOpen;
  }

  ngOnDestroy(): void {
    this.subscriptionHandlerService.unsubscribeAllFromComponent(this.componentName);
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    // Clears the timeouts on navigation start
    const navigationStart = onNavigationStart(this.router, this.destroy$, () => {
      this.timeouts = clearTimeouts(this.timeouts);
    });
    this.subscriptionHandlerService.addSubscription(this.componentName, 'navigationStart', navigationStart);
    this.client = getAppClient();
    this.showButton = this.client === 'dtc'; // TODO: Check user suscription
    this.setProfileDetails();
    this.trackLanguageChange();
    this.trackProfileChange();
    this.trackIndexChange();
    this.trackTabsChange();
  }

  ngAfterViewInit(): void {
    this.timeouts.push(
      setTimeout(() => {
        this.setTabMenuIndex();
      }, 10)
    );
  }

  reload(child: string): void {
    if (child === 'SEARCH') {
      this.dataService.reloadSearch(); // listened by search component
    } else if (child === 'SETTINGS') {
      this.dataService.reloadSettings(); // listened by search component
    } else if (child === 'COLLECTION') {
      this.dataService.reloadCollection();
    } else if (child === 'EPG') {
      this.dataService.reloadEPG(); // listened by EPG component
    } else {
      this.dataService.reloadStorefront();
    }
  }

  /**
   * Used by reuse strategy to stop player when the component is detached
   * This function should be implemented in all components that use the reuse strategy
   */
  stop(): void {
    this.dataService.stopEpg();
  }

  /**
   * Fired when some children of menu is blured
   */
  focusOut(event: FocusEvent): void {
    let i: number;
    const children = this.tvMenu.nativeElement.children;
    for (i = 0; i < children.length; i++) {
      if (this.activeElement === children[i]) {
        return;
      }
    }
    this.menuActive = false;
    if (!(event.relatedTarget as HTMLElement)?.classList.contains('menu__item')) {
      this.setTabMenuIndex();
    }
  }

  /**
   * Fired when some children of menu is focused
   */
  focusIn(): void {
    this.menuActive = true;
    if (!this.enterMenu) {
      this.setProfileDetails();
      this.enterMenu = true;
    }
  }

  /**
   * Fired when some children of menu is over the mouse
   */
  mouseIn(): void {
    this.focusIn();
  }

  mouseOut(): void {
    let i: number;
    const children = this.tvMenu.nativeElement.children;
    for (i = 0; i < children.length; i++) {
      if (this.activeElement === children[i]) {
        return;
      }
    }
    this.menuActive = false;
  }

  /**
   * Set index of focus
   */
  focusItem(event: FocusEvent, index: number): void {
    if (this.menuActive) {
      this.navService.focusElement(event.target as HTMLElement, 0, index);
    }
  }

  /**
   * Update current focused tab id
   *
   * @param tab
   */
  focusTab(tab: TabVm): void {
    this.focusedTab = tab.id;
  }

  blurActiveElement(): void {
    if (this.menuActive) {
      this.navService.blurActiveElement();
    }
  }

  onBackButton(): void {
    // Exit confirmation is open
    if (!this.showBack) {
      this.exitService.closeExitConfirmation();
      console.log('Exit confirm', this.dataService.activeTab, this.focusedTab, this.tabs);
      if (this.dataService.activeTab === 'myhub') {
        this.navService.focusPage('settings-menu');
      } else if (this.dataService.activeTab === 'epg') {
        this.navService.focusPage('page-container');
      } else if (this.dataService.activeTab === 'search') {
        this.navService.focusPage('keyboard-grid');
      } else {
        this.navService.focusContainer('horizontal-focusable-container', true);
      }
    } else {
      if (this.menuActive) {
        this.navService.setIndexContainer('tv-menu', this.relativeVerticalIndex);
        this.exitService.showExitConfirmation();
      } else {
        this.navService.focusContainer('tv-menu', false);
      }
    }
  }

  /**
   * Manage menu redirections
   *
   * @param option menu option
   * @param event native event
   * @param tabId tabId option
   */
  redirectTo(option: MenuOption, event: any, tab?: TabVm): void {
    // stop propagation and avoid fire toggleMenu
    event?.stopPropagation();
    this.menuActive = false;
    this.manageNavigation(option, tab?.id);
    this.dataService.activeTab = option;
    localStorage.setItem(LocalStorageKeys.CLEAN_ROUTES, 'true');
    switch (option) {
      case 'tab':
        if (tab && tab.id !== this.dataService.activeTab) {
          this.setTabsData(tab);
          this.sendNewrelicTabChange(tab);
          this.navService.setIndexContainer('visual-bars', 0);
          this.router.navigate([tab.id], { state: { tabId: tab.id } });
        }
        break;
      case 'search':
        this.setTabsData({ id: 'search', name: 'Search' });
        this.sendNewrelicMenuChange({ id: 'search', name: 'Search' });
        this.router.navigate(['search']);
        break;
      case 'myhub':
        this.sendNewrelicMenuChange({ id: 'myhub', name: 'myhub' });
        // send profile data
        this.setTabsData({ id: 'myhub', name: 'myhub' });
        this.router.navigate(['myhub']);
        break;
      case 'epg':
        this.sendNewrelicMenuChange({ id: 'epg', name: 'epg' });
        this.screenService.setCurrentScreen(PageType.Epg);
        this.router.navigate(['epg']);
        break;
      case 'my-content':
        this.setTabsData({ id: 'my-content', name: 'my-content' });
        this.sendNewrelicMenuChange({ id: 'my-content', name: 'my-content' });
        this.screenService.setCurrentScreen(PageType.MyContent);
        this.router.navigate(['myaccount']);
        break;
    }
    this.enterMenu = false;
    this.setTabMenuIndex();
  }

  /**
   * Subscribe to language changes
   */
  private trackLanguageChange(): void {
    const languageChange: Subscription = this.languageService
      .getObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((resetIndex) => {
        this.loadTabs(resetIndex);
      });
    this.subscriptionHandlerService.addSubscription(this.componentName, 'languageChange', languageChange);
  }

  /**
   * Subscribe to profile changes
   */
  private trackProfileChange(): void {
    const selectedProfile: Subscription = this.userState.selectedProfile.pipe(takeUntil(this.destroy$)).subscribe((profile) => {
      this.selectedProfile = profile;
    });
    this.subscriptionHandlerService.addSubscription(this.componentName, 'selectedProfile', selectedProfile);
  }

  private trackIndexChange(): void {
    const menuIndexReload: Subscription = this.dataService.menuIndexReload.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setTabMenuIndex();
    });
    this.subscriptionHandlerService.addSubscription(this.componentName, 'menuIndexReload', menuIndexReload);
  }

  /**
   * Detect tabs changes
   */
  private trackTabsChange(): void {
    const tabsUpdated: Subscription = this.tabService.tabsUpdated.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tveImgSrc = this.adobeService.mvpdImg;
      this.loadTabs();
      this.setTabMenuIndex();
    });
    this.subscriptionHandlerService.addSubscription(this.componentName, 'tabsUpdated', tabsUpdated);
  }

  /**
   * Get tabs from service and set active tab
   */
  private loadTabs(resetIndex = false): void {
    this.tabs = this.tabService.getTabs();
    //default first tab
    if (!resetIndex) this.navService.setIndexContainer('tv-menu', this.relativeVerticalIndex);
    this.changeRef.detectChanges();
  }

  /**
   * if the user is logged in, get profiles
   * if is an anonymous user, reset the menu
   */
  private setProfileDetails(): void {
    this.tveImgSrc = this.adobeService.mvpdImg;
    this.isLoggedIn = isLoggedIn(this.localStorage);
    if (this.isLoggedIn) {
      this.getProfiles();
    } else {
      this.resetMenu();
    }
  }

  /**
   * Get profiles from account
   * if there is an error, show popup and logout
   * if there is not an error, set profile info
   */
  private getProfiles(): void {
    this.selectedProfile = this.profileService.getSelectedProfile();
  }

  /**
   * Reset menu values as anonymous user
   */
  private resetMenu(): void {
    this.isLoggedIn = false;
    this.selectedProfile = undefined;
  }

  private sendNewrelicMenuChange(tab: { id: string; name: string }): void {
    this.newrelic.store.setScreenInfo({
      currentScreen: this.screenService.currentScreen,
      previousScreen: this.screenService.previousScreen,
    });
    this.newrelic.store.setTabInfo({
      tabID: tab.id,
      tabName: tab.name,
    });
    this.newrelic.sendEvent(NewrelicEvent.MENU_TAB_CHANGE);
  }

  private sendNewrelicTabChange(tab: TabVm): void {
    this.newrelic.store.setScreenInfo({
      currentScreen: this.screenService.currentScreen,
      previousScreen: this.screenService.previousScreen,
    });
    this.newrelic.store.setTabInfo({
      tabID: tab.id,
      tabName: tab.name,
    });

    this.newrelic.sendEvent(NewrelicEvent.STOREFRONT_TAB_CHANGE);
  }

  /**
   * Check option selected and manage navigation indexes and focus
   */
  private manageNavigation(url: MenuOption, tabId: string): void {
    // restart vertical index in my-content
    if (!url.includes('my-content')) {
      this.navService.setIndexContainer('my-content container-all', 0);
      this.dataService.reloadMyContent = true;
    }
    // restart navigation
    if (!this.router.url.includes(tabId) && !url.includes('linkAccount')) {
      //restart sf navigation from reuse strategy
      this.localStorage.setItem(LocalStorageKeys.TAB_CHANGED, 'true');
    } else {
      // if is the same tab, search last focused item
      this.navService.focusSectionPage('storefront container-all');
    }
    this.navService.blurActiveElement();
  }

  private setTabMenuIndex(): void {
    let index = this.tabs.findIndex((tab) => tab.id === this.dataService.activeTab);
    if (index >= 0) {
      index += this.relativeVerticalIndex;
      this.navService.setIndexContainer('tv-menu', index);
    } else {
      this.navService.setIndexContainer('tv-menu', this.relativeVerticalIndex);
    }
  }

  private setTabsData(tabData): void {
    this.dataService.activeTab = tabData?.id;
    this.tabService.currentTabId = tabData?.id;
    this.tabService.currentTabName = tabData?.name;
  }
}
